'use client';

import { AppWindow, Bot } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import {
  openDiscover,
  openDiscoverOn,
  useDiscoverOpen,
} from '@/components/workspace/discover/discover-events';

/**
 * The empty workspace's greeting, under the V2 rail.
 *
 * Under railV2 the only empty state used to be the ONE-SHOT Discover
 * auto-open (discover-host.tsx): close it, arrive on a spawn-bearing deep
 * link, or empty the workspace later, and the canvas was a blank with no
 * hint of how anything gets on it (the Revent e2e re-run of 2026-09-26: "a
 * new workspace is a blank canvas with no add-app hint"). The inline card
 * (`CanvasEmptyState`) renders only for `!railV2`, so nothing covered the
 * default rail at all.
 *
 * It started as one muted line and an "Add an app" button, which read as an
 * afterthought on a screen that is the first thing a new account sees
 * (2026-09-30 redesign). Now it is a title, one line of what to do, the two
 * things there are to do (an app, or an agent) and the quick start guide
 * (docs/public/guides/quick-start.mdx) for someone who has never seen the
 * product. Both buttons open Discover: "Find an agent" starts it on the
 * Agents chip instead of Apps.
 *
 * It is safe for anonymous visitors (DiscoverHost mounts for them too, and a
 * spawn mints a guest through the store fetch wrapper) and calls no panel
 * event, for the same reason the old card could not (the panel host does not
 * mount for a null user). The canvas gates it on `railV2 && workspaceIsEmpty
 * && !overviewMode && !fullscreenItemId`; the Discover check lives here
 * because it is a hook.
 */
export function EmptyWorkspaceHint() {
  const t = useT();
  const discoverOpen = useDiscoverOpen();
  if (discoverOpen) return null;
  return (
    <div
      className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center px-6"
      // Centred in the VISIBLE canvas: on a phone the tab bar covers the
      // bottom edge (the left edge in landscape) and publishes its size, the
      // same insets the Discover overlay clears (discover-host.tsx).
      style={{
        paddingBottom: 'calc(var(--ws-bar-b, 0px) + env(safe-area-inset-bottom))',
        paddingLeft: 'calc(var(--ws-bar-l, 0px) + 1.5rem)',
      }}
      data-testid="empty-workspace-hint"
    >
      {/* Only the controls take the pointer. The text is a large block in
          the middle of the canvas, and over it a pan, a pinch or the canvas
          right-click menu should still reach the canvas underneath. */}
      <div className="relative flex max-w-md flex-col items-center text-center">
        {/* The canvas grid's marks sat right behind the line and the link and
            read as stray characters. A soft, edge-faded blur softens them
            without painting a colour: the canvas centre is not the
            background token in dark mode, so a colour halo would show as a
            disc. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-x-16 -inset-y-12 -z-10 backdrop-blur-[6px] [mask-image:radial-gradient(closest-side,black_60%,transparent)]"
        />
        <h2 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          {t('workspace.empty.quietTitle')}
        </h2>
        <p className="mt-3 text-sm text-muted-foreground sm:text-base">{t('workspace.empty.quietBody')}</p>
        <div className="pointer-events-auto mt-7 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => openDiscover()}
            className="inline-flex h-10 items-center gap-2 rounded-lg bg-foreground px-4 text-sm font-medium text-background shadow-sm transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            data-testid="empty-workspace-add-app"
          >
            <AppWindow className="h-4 w-4" />
            {t('workspace.empty.findApp')}
          </button>
          <button
            type="button"
            onClick={() => openDiscoverOn('agents')}
            // The border is drawn from the foreground: the border token all but
            // vanishes on the dark canvas, and the button then read as filled.
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-foreground/15 bg-background px-4 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring dark:border-foreground/25"
            data-testid="empty-workspace-find-agent"
          >
            <Bot className="h-4 w-4" />
            {t('workspace.empty.findAgent')}
          </button>
        </div>
        {/* A new tab: the workspace stays where it is, and in an installed
            app a same-tab jump to the docs leaves no way back. */}
        <a
          href="/docs/guides/quick-start"
          target="_blank"
          rel="noopener noreferrer"
          className="pointer-events-auto mt-6 text-xs text-foreground/80 underline underline-offset-4 transition-colors hover:text-foreground sm:text-sm"
          data-testid="empty-workspace-guide"
        >
          {t('workspace.empty.quickStart')}
        </a>
      </div>
    </div>
  );
}
